import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { createReportMutation, CreateReportInput } from '../../../mutations/reports';
import {
  deleteReportMutation,
  DeleteReportInput,
  duplicateReportMutation,
  DuplicateReportInput,
  lockReportMutation,
  LockReportInput,
  publishReportMutation,
  PublishReportInput
} from '../../../mutations/reportActions';

export interface ModifyReportViewState extends ApiViewState {
  duplicatedReportId?: string;
  errorCreating: Error | null;
  errorDeleting: Error | null;
  errorDuplicating: Error | null;
  errorLocking: Error | null;
  errorPublishing: Error | null;
  isCreating: boolean;
  isDeleting: boolean;
  isDuplicating: boolean;
  isLocking: boolean;
  isPublishing: boolean;
  newReportId?: string;
}

export class ModifyReportView extends ApiView {
  private createMutation: ReturnType<typeof createReportMutation>;
  private deleteMutation: ReturnType<typeof deleteReportMutation>;
  private duplicateMutation: ReturnType<typeof duplicateReportMutation>;
  private lockMutation: ReturnType<typeof lockReportMutation>;
  private publishMutation: ReturnType<typeof publishReportMutation>;

  public state: ModifyReportViewState = {
    ...getDefaultApiViewState(),
    duplicatedReportId: undefined,
    errorCreating: null,
    errorDeleting: null,
    errorDuplicating: null,
    errorLocking: null,
    errorPublishing: null,
    isCreating: false,
    isDeleting: false,
    isDuplicating: false,
    isLocking: false,
    isPublishing: false,
    newReportId: undefined
  };

  constructor(workspaceId: string, organisationId: string, isConsolidation = false) {
    super();

    this.createMutation = this.addMutation(createReportMutation(workspaceId, organisationId, isConsolidation));
    this.deleteMutation = this.addMutation(deleteReportMutation(workspaceId, organisationId, isConsolidation));
    this.duplicateMutation = this.addMutation(duplicateReportMutation(workspaceId, organisationId, isConsolidation));
    this.lockMutation = this.addMutation(lockReportMutation(workspaceId, organisationId, isConsolidation));
    this.publishMutation = this.addMutation(publishReportMutation(workspaceId, organisationId, isConsolidation));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createReport(data: CreateReportInput) {
    return this.createMutation.mutate(data);
  }

  public deleteReport(data: DeleteReportInput) {
    return this.deleteMutation.mutate(data);
  }

  public duplicateReport(data: DuplicateReportInput) {
    return this.duplicateMutation.mutate(data);
  }

  public lockReport(data: LockReportInput) {
    return this.lockMutation.mutate(data);
  }

  public publishReport(data: PublishReportInput) {
    return this.publishMutation.mutate(data);
  }

  public resetMutations() {
    this.createMutation.reset();
    this.deleteMutation.reset();
    this.duplicateMutation.reset();
    this.lockMutation.reset();
    this.publishMutation.reset();
    this.updateState();
  }

  // ****************************
  // * Private / Protected API *
  // ****************************
  protected getNewState(): ModifyReportViewState {
    return {
      ...super.getNewState(),
      duplicatedReportId: this.duplicateMutation.getCurrentResult().data?.id,
      errorCreating: this.createMutation.getCurrentResult().error,
      errorDeleting: this.deleteMutation.getCurrentResult().error,
      errorDuplicating: this.duplicateMutation.getCurrentResult().error,
      errorLocking: this.lockMutation.getCurrentResult().error,
      errorPublishing: this.publishMutation.getCurrentResult().error,
      isCreating: this.createMutation.getCurrentResult().status === 'pending',
      isDeleting: this.deleteMutation.getCurrentResult().status === 'pending',
      isDuplicating: this.duplicateMutation.getCurrentResult().status === 'pending',
      isLocking: this.lockMutation.getCurrentResult().status === 'pending',
      isPublishing: this.publishMutation.getCurrentResult().status === 'pending',
      newReportId: this.createMutation.getCurrentResult().data?.id
    };
  }
}
